import React, { useState, memo } from 'react'
import { Button, Modal } from '~elements'
import { Col, Container, Row } from 'react-grid-system'
import SelectedEvent from '../SelectedEvent/SelectedEvent'
import { useModal } from 'utils/hooks/useModal'
import dayjs from 'utils/dayjs'
import styles from './EventsListItem.module.scss'
import { EventsByDate, FormattedEvent } from 'components/modules/Calendar/types/Event'
import { DATE_FORMAT } from '~constants'
import { getCtaData } from 'components/modules/Calendar/utils'
import { ArtistProps, CtaData } from 'components/modules/Calendar/types'
import { DATE_FORMATTER, TM_CTA_TITLE_DEFAULT } from 'constants/calendar'

interface EventListItemProps {
  date: string
  hidePricing: boolean
  modalData: {
    artist: ArtistProps
    ctaSoldOutText?: string
    showPriceInModal?: boolean
  }
  overrideApiSoldOutStatus: boolean
  filteredCalendarData: EventsByDate
  paginatedEventsByDate: string[]
}

const EventListItem = ({
  date,
  modalData,
  overrideApiSoldOutStatus,
  filteredCalendarData,
  paginatedEventsByDate,
  hidePricing
}: EventListItemProps) => {
  if (!paginatedEventsByDate.includes(date)) return null

  const eventList = filteredCalendarData[date] || []
  const eventDate = dayjs(date)

  const formattedDate = eventDate.format(DATE_FORMATTER.DATE)
  const formattedDay = eventDate.format(DATE_FORMATTER.SHORT_DAY)
  const isAllSoldOut = eventList.every(event => event?.isSoldOut)
  const { min: minPrice, max: maxPrice } = eventList[0]?.price || {}
  const priceText = minPrice && maxPrice ? (minPrice === maxPrice ? `$${minPrice}` : `$${minPrice} - $${maxPrice}`) : ''

  const { isModalOpen, openModal, closeModal } = useModal()
  const [selectedEvent, setSelectedEvent] = useState<FormattedEvent | null>(null)
  const [ctaData, setCtaData] = useState<CtaData>({ url: null, title: null })

  const handleButtonClick = (event: FormattedEvent) => {
    setSelectedEvent(event)
    const ctaInfo = getCtaData(event, modalData.artist, !overrideApiSoldOutStatus)
    setCtaData(ctaInfo)
    openModal()
  }

  return (
    <li className={styles['list-item']}>
      <Container className='container container--width-100'>
        <Row>
          {/* Date Column */}
          <Col md={2} className='align-contents-vertical-top'>
            <div className={styles['list-item-date-holder']}>
              <div className={styles['list-item__date']}>
                <span className={styles['list-item__formatted-date']}>{formattedDate}</span>
                <span className={styles['list-item__separator']}>|</span>
                <span className={styles['list-item__formatted-day']}>{formattedDay}</span>
              </div>
            </div>
          </Col>

          {/* Price Column */}
          <Col md={3} className='align-contents-vertical-top'>
            {!hidePricing && (
              <div className={styles['list-item-date-holder']}>
                <p className={styles.price}>
                  {isAllSoldOut && !overrideApiSoldOutStatus ? (
                    <span>{modalData?.ctaSoldOutText ?? TM_CTA_TITLE_DEFAULT.sold_out}</span>
                  ) : priceText ? (
                    <>
                      Tickets from <span>{priceText}</span>
                    </>
                  ) : null}
                </p>
              </div>
            )}
          </Col>

          {/* CTA Buttons */}
          <Col md={7}>
            <div className={styles['list-item-ctas']}>
              {eventList.map(event => {
                const time = dayjs(event.dateTime).format(DATE_FORMAT.TWELVE_HRS)
                if (!time || event?.isPast) return null
                const eventShouldAppearSoldOut = event.isSoldOut && !overrideApiSoldOutStatus

                return (
                  <div key={event.id}>
                    <Button
                      color='light'
                      disabled={eventShouldAppearSoldOut}
                      fill='hollow'
                      size='sm'
                      title={time}
                      onClick={!eventShouldAppearSoldOut ? () => handleButtonClick(event) : undefined}
                      analytics={{ skipEvent: true }}
                    />
                  </div>
                )
              })}
            </div>
          </Col>
        </Row>

        {/* Modal */}
        {selectedEvent && (
          <Modal isOpen={isModalOpen} handleModalClose={closeModal} closeOnOutsideClick={false} type='event_calendar'>
            <SelectedEvent eventInfo={selectedEvent} ctaData={ctaData} showPriceInModal={modalData.showPriceInModal} />
          </Modal>
        )}
      </Container>
    </li>
  )
}

export default memo(EventListItem)
